// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI (owner, 11 October 2026) — the clouds on the bare globe come from
 * AYNI's own real-time stream "Global Cloud Cover", one frame per visit.
 *
 * The stream is value-encoded VP9 DASH (luma = cloud cover, 0–100 %). Its
 * newest segment begins with a keyframe, the only frame that decodes on
 * its own, so only that frame's bytes are fetched: the release
 * (`latest.json` → `release.json`), the first few KB of the newest
 * segment for its `moof`, then the keyframe by an HTTP range. VP9 needs
 * no initialization segment. WebCodecs decodes it; the frame's calibration
 * strip says what the decoder did to the codes (decoderRange.ts: Chrome
 * expands a drawn frame as limited range), and the luma becomes a
 * greyscale picture (clear sky black, full cover white) — the shape the
 * two globes' cloud code already turns into alpha (earthTileLayer,
 * photorealEarth). The frame is up to one segment old (18 frames of 10
 * minutes); for a decorative layer that is enough.
 *
 * Shared: both globes get the same canvas and the frame is fetched once.
 * Without the stream host configured, WebCodecs or VP9, there are no
 * clouds — never another host.
 */
import { parseReleaseEncoding, valueAtCode, type ReleaseEncoding } from './dashRelease'
import { bandCodes, fitDecoderRange, inDecodedSpace } from './decoderRange'
import { logger } from '../utils/logger'
import { streamAuthFetch } from './streamToken'
import { streamHostBase } from './streamHost'

/** The cloud stream's release pointer, under the stream host. */
export const CLOUD_STREAM_LATEST = 'global/realtime/noaa-sos/clouds/latest.json'

/** How much of a segment's start is read for its `moof`. */
const HEAD_BYTES = 8192

function rec(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

/** The cloud stream's `latest.json` URL on the stream host (`streamHost.ts`), or null for a blank host given explicitly. */
export function cloudStreamUrl(base: string | undefined = streamHostBase()): string | null {
  const root = base?.trim()
  if (!root) return null
  return new URL(CLOUD_STREAM_LATEST, root.endsWith('/') ? root : `${root}/`).toString()
}

/** What the newest keyframe needs: its segment, the codec and the frame size. */
export interface NewestSegment {
  readonly mediaUrl: string
  readonly codec: string
  readonly width: number
  readonly height: number
  readonly encoding: ReleaseEncoding
}

/** The release's newest segment (the tail when there is one), or null when the release cannot give one. */
export function newestSegment(release: unknown, releaseUrl: string): NewestSegment | null {
  const body = rec(release)
  const segments = Array.isArray(body?.segments) ? body.segments : []
  const last = rec(segments[segments.length - 1])
  const media = rec(last?.media)?.url
  const rep = rec(body?.representation)
  const encoding = parseReleaseEncoding(release)
  if (typeof media !== 'string' || !media || !rep || !encoding) return null
  const { codec, width, height } = rep
  if (typeof codec !== 'string' || !codec.startsWith('vp09')) return null
  if (typeof width !== 'number' || typeof height !== 'number' || width <= 0 || height <= 0) return null
  return { mediaUrl: new URL(media, releaseUrl).toString(), codec, width, height, encoding }
}

/**
 * Where the first sample of a fragmented MP4 segment lies, from the bytes
 * up to its `moof`: [start, end) in the file. Null when the head holds no
 * complete `moof`, or its first sample is not a sync (key) frame.
 */
export function firstSampleRange(head: Uint8Array): { start: number; end: number } | null {
  const view = new DataView(head.buffer, head.byteOffset, head.byteLength)
  const type = (at: number) => String.fromCharCode(head[at], head[at + 1], head[at + 2], head[at + 3])
  // Boxes from `from` to `to`; calls `visit` with each one's type, start and end.
  const boxes = (from: number, to: number, visit: (t: string, start: number, end: number) => boolean | void): boolean => {
    let at = from
    while (at + 8 <= to) {
      let size = view.getUint32(at)
      let header = 8
      if (size === 1) {
        if (at + 16 > to) return false
        size = Number(view.getBigUint64(at + 8))
        header = 16
      }
      if (size < header) return false
      if (visit(type(at + 4), at, at + size) === true) return true
      at += size
    }
    return false
  }
  let result: { start: number; end: number } | null = null
  boxes(0, head.length, (t, moofStart, moofEnd) => {
    if (t !== 'moof') return
    if (moofEnd > head.length) return true
    boxes(moofStart + 8, moofEnd, (t2, trafStart, trafEnd) => {
      if (t2 !== 'traf') return
      let base = moofStart
      let defaultSize: number | null = null
      let defaultFlags: number | null = null
      boxes(trafStart + 8, trafEnd, (t3, s) => {
        const flags = view.getUint32(s + 8) & 0xffffff
        if (t3 === 'tfhd') {
          let p = s + 16 // after the track id
          if (flags & 0x1) { base = Number(view.getBigUint64(p)); p += 8 }
          if (flags & 0x2) p += 4
          if (flags & 0x8) p += 4
          if (flags & 0x10) { defaultSize = view.getUint32(p); p += 4 }
          if (flags & 0x20) defaultFlags = view.getUint32(p)
          return
        }
        if (t3 !== 'trun' || view.getUint32(s + 12) < 1) return
        let p = s + 16
        let offset = 0
        if (flags & 0x1) { offset = view.getInt32(p); p += 4 }
        let sampleFlags = defaultFlags
        if (flags & 0x4) { sampleFlags = view.getUint32(p); p += 4 }
        if (flags & 0x100) p += 4
        let size = defaultSize
        if (flags & 0x200) { size = view.getUint32(p); p += 4 }
        if (!(flags & 0x4) && flags & 0x400) sampleFlags = view.getUint32(p)
        // sample_is_non_sync_sample (bit 16): a frame that needs others.
        if (size === null || size <= 0 || (sampleFlags !== null && sampleFlags & 0x10000)) return true
        result = { start: base + offset, end: base + offset + size }
        return true
      })
      return true
    })
    return true
  })
  return result
}

/**
 * The data region of a decoded frame's luma as greyscale RGBA: each code's
 * value as a fraction of the stream's range (0 = clear, 255 = full cover);
 * no data and values the presentation leaves transparent are black.
 */
export function cloudGrey(luma: Uint8Array, stride: number, enc: ReleaseEncoding): { width: number; height: number; data: Uint8ClampedArray<ArrayBuffer> } {
  const { x, y, width, height } = enc.dataRegion
  const span = enc.vmax - enc.vmin
  const below = enc.transparentBelowValue ?? -Infinity
  const grey = new Uint8Array(256)
  for (let code = 0; code < 256; code++) {
    const value = valueAtCode(enc, code)
    grey[code] = value === null || value < below || span <= 0
      ? 0
      : Math.round(255 * Math.min(1, Math.max(0, (value - enc.vmin) / span)))
  }
  const px = new Uint8ClampedArray(width * height * 4)
  for (let row = 0; row < height; row++) {
    const src = (y + row) * stride + x
    let dst = row * width * 4
    for (let col = 0; col < width; col++, dst += 4) {
      const g = grey[luma[src + col]]
      px[dst] = g
      px[dst + 1] = g
      px[dst + 2] = g
      px[dst + 3] = 255
    }
  }
  return { width, height, data: px }
}

/**
 * The encoding in the space this frame's codes arrived in, measured on its
 * calibration strip (decoderRange.ts): a drawn frame can come back expanded
 * as limited range (Chrome), the raw luma plane as published. No strip, or
 * one that does not fit a line: the codes as published.
 */
export function calibratedEncoding(enc: ReleaseEncoding, luma: Uint8Array, stride: number, width: number): ReleaseEncoding {
  const cal = enc.calibration
  if (!cal) return enc
  const row = luma.subarray(cal.row * stride, cal.row * stride + width)
  if (row.length < width) return enc
  const range = fitDecoderRange(cal.codes, bandCodes(row, cal.codes.length))
  return range ? inDecodedSpace(enc, range) : enc
}

/** One keyframe's luma plane, decoded by WebCodecs; null when this browser cannot. */
async function decodeLuma(chunk: Uint8Array, seg: NewestSegment): Promise<{ luma: Uint8Array; stride: number; width: number } | null> {
  if (typeof VideoDecoder === 'undefined' || typeof EncodedVideoChunk === 'undefined') return null
  const config: VideoDecoderConfig = { codec: seg.codec, codedWidth: seg.width, codedHeight: seg.height }
  const support = await VideoDecoder.isConfigSupported(config).catch(() => null)
  if (!support?.supported) return null
  const frame = await new Promise<VideoFrame>((resolve, reject) => {
    let out: VideoFrame | null = null
    const decoder = new VideoDecoder({ output: (f) => { out ??= f }, error: reject })
    decoder.configure(config)
    decoder.decode(new EncodedVideoChunk({ type: 'key', timestamp: 0, data: chunk }))
    decoder.flush()
      .then(() => (out ? resolve(out) : reject(new Error('the keyframe decoded to nothing'))))
      .catch(reject)
      .finally(() => { if (decoder.state !== 'closed') decoder.close() })
  })
  try {
    // Planar and semi-planar formats keep luma first: read it exactly.
    if (frame.format && /^(I4|NV12)/.test(frame.format)) {
      const buffer = new Uint8Array(frame.allocationSize())
      const layout = await frame.copyTo(buffer)
      return { luma: buffer.subarray(layout[0].offset), stride: layout[0].stride, width: frame.visibleRect?.width ?? frame.codedWidth }
    }
    // A GPU-backed frame: draw it and read red, which equals luma for grey.
    const canvas = document.createElement('canvas')
    canvas.width = frame.displayWidth
    canvas.height = frame.displayHeight
    const ctx = canvas.getContext('2d', { willReadFrequently: true })
    if (!ctx) return null
    ctx.drawImage(frame, 0, 0)
    const rgba = ctx.getImageData(0, 0, canvas.width, canvas.height).data
    const luma = new Uint8Array(canvas.width * canvas.height)
    for (let i = 0; i < luma.length; i++) luma[i] = rgba[i * 4]
    return { luma, stride: canvas.width, width: canvas.width }
  } finally {
    frame.close()
  }
}

/** The newest cloud frame as a greyscale canvas, or null; fetched by `loadCloudFrame`. */
export async function fetchCloudFrame(latestUrl: string, fetchImpl: typeof fetch = fetch): Promise<HTMLCanvasElement | null> {
  const json = async (url: string) => {
    const res = await fetchImpl(url, { headers: { Accept: 'application/json' }, cache: 'no-cache' })
    if (!res.ok) throw new Error(`${url} answered ${res.status}`)
    return res.json() as Promise<unknown>
  }
  const pointer = rec(rec(await json(latestUrl))?.releaseDescriptor)?.url
  if (typeof pointer !== 'string' || !pointer) throw new Error(`${latestUrl} names no release`)
  const releaseUrl = new URL(pointer, latestUrl).toString()
  const seg = newestSegment(await json(releaseUrl), releaseUrl)
  if (!seg) throw new Error(`${releaseUrl} has no VP9 segment this globe can read`)

  const bytes = async (start: number, end: number) => {
    const res = await fetchImpl(seg.mediaUrl, { headers: { Range: `bytes=${start}-${end - 1}` } })
    if (res.status !== 206 && res.status !== 200) throw new Error(`${seg.mediaUrl} answered ${res.status}`)
    const all = new Uint8Array(await res.arrayBuffer())
    // A host that ignores the range sends the whole file.
    return res.status === 200 ? all.subarray(start, end) : all
  }
  const range = firstSampleRange(await bytes(0, HEAD_BYTES))
  if (!range) throw new Error(`${seg.mediaUrl} does not start with a keyframe`)
  const decoded = await decodeLuma(await bytes(range.start, range.end), seg)
  if (!decoded) {
    logger.info('[clouds] This browser cannot decode the cloud stream (WebCodecs VP9); no clouds')
    return null
  }
  const grey = cloudGrey(decoded.luma, decoded.stride, calibratedEncoding(seg.encoding, decoded.luma, decoded.stride, decoded.width))
  const canvas = document.createElement('canvas')
  canvas.width = grey.width
  canvas.height = grey.height
  canvas.getContext('2d')?.putImageData(new ImageData(grey.data, grey.width, grey.height), 0, 0)
  return canvas
}

let pending: Promise<HTMLCanvasElement | null> | null = null

/** The newest cloud frame, fetched once per page and shared by every globe; null when there is none. */
export function loadCloudFrame(): Promise<HTMLCanvasElement | null> {
  pending ??= (async () => {
    const url = cloudStreamUrl()
    if (!url) return null
    try {
      // AYNI (S-02): the keyframe's latest.json and range read carry the stream token once the host needs one.
      return await fetchCloudFrame(url, streamAuthFetch)
    } catch (err) {
      logger.warn('[clouds] Could not load the newest cloud frame:', err)
      return null
    }
  })()
  return pending
}

/** For tests. */
export function resetCloudFrame(): void {
  pending = null
}
